/**
 * The Interests tab's calls — the sales desk.
 *
 * Same shape as `tenants-api.ts`: every body is parsed against the shared
 * contract before it leaves, so a drifted form fails here with the field named
 * rather than as a 400, and every response is parsed on the way back in.
 */
import { z } from 'zod';

import {
  addInterestNoteRequestSchema,
  createInterestRequestSchema,
  interestDetailSchema,
  interestListResponseSchema,
  updateInterestStatusRequestSchema,
  type AddInterestNoteRequest,
  type CreateInterestRequest,
  type InterestDetail,
  type InterestFilter,
  type InterestListResponse,
  type UpdateInterestStatusRequest,
} from '@wend/shared';

import { INTERESTS_PATH, interestNotesPath, interestPath, interestStatusPath } from './admin-endpoints';
import { apiClient } from './api-client';

export interface InterestListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly status: InterestFilter;
  /** Name, email, organisation or phone, case-insensitive. */
  readonly search?: string;
}

export async function listInterests(
  params: InterestListParams,
  signal?: AbortSignal,
): Promise<InterestListResponse> {
  return apiClient.request({
    path: INTERESTS_PATH,
    schema: interestListResponseSchema,
    query: {
      page: params.page,
      pageSize: params.pageSize,
      status: params.status,
      search: params.search,
    },
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function getInterest(interestId: string, signal?: AbortSignal): Promise<InterestDetail> {
  return apiClient.request({
    path: interestPath(interestId),
    schema: interestDetailSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function createInterest(input: CreateInterestRequest): Promise<InterestDetail> {
  return apiClient.request({
    path: INTERESTS_PATH,
    method: 'POST',
    body: createInterestRequestSchema.parse(input),
    schema: interestDetailSchema,
  });
}

const statusChangeResponseSchema = z.object({
  interest: interestDetailSchema,
  /** False when it already had that status: nothing was written, not even the note. */
  changed: z.boolean(),
});
export type InterestStatusChange = z.infer<typeof statusChangeResponseSchema>;

export async function changeInterestStatus(
  interestId: string,
  input: UpdateInterestStatusRequest,
): Promise<InterestStatusChange> {
  return apiClient.request({
    path: interestStatusPath(interestId),
    method: 'PATCH',
    body: updateInterestStatusRequestSchema.parse(input),
    schema: statusChangeResponseSchema,
  });
}

export async function addInterestNote(
  interestId: string,
  input: AddInterestNoteRequest,
): Promise<InterestDetail> {
  return apiClient.request({
    path: interestNotesPath(interestId),
    method: 'POST',
    body: addInterestNoteRequestSchema.parse(input),
    schema: interestDetailSchema,
  });
}
